const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const sanitizeSegment = (value: string | null | undefined): string => value?.trim() ?? '';

const SLUG_INPUT_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const sanitizeSlugCandidate = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/(^-+)|(-+$)/g, '');

export const isValidSlugCandidate = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }
  return SLUG_INPUT_PATTERN.test(value);
};

const encodeSegment = (value: string): string => encodeURIComponent(value);

export const isUuid = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }
  return UUID_REGEX.test(value.trim());
};

export const normalizeSlug = (slugOrId: string): string => slugOrId.toLowerCase().trim();

export const getSlugOrId = (entity: { id: string | null; slug?: string | null }): string => {
  const slug = sanitizeSegment(entity.slug);
  if (slug) return slug;
  if (!entity.id) {
    throw new Error('Cannot build path: both id and slug are null/empty');
  }
  return entity.id;
};

/**
 * Whether getSlugOrId / getProfilePath / getEventPath / … can build a path for
 * this entity (a slug or an id is present). Use this to gate navigation
 * affordances so a degraded summary (both id and slug null) renders
 * non-interactively instead of throwing in the path builder on click. Mirrors
 * getSlugOrId's slug-or-id logic so the guard and the builder can't drift.
 */
export const isPathBuildable = <T extends { id: string | null; slug?: string | null }>(
  entity: T | null | undefined,
): entity is T => Boolean(entity && (sanitizeSegment(entity.slug) || entity.id));

const buildEntitySegment = (entity: { id: string | null; slug?: string | null }): string => {
  return encodeSegment(getSlugOrId(entity));
};

export const getEventPath = (event: { id: string | null; slug?: string | null }): string =>
  `/events/${buildEntitySegment(event)}`;

export const getQuestionPath = (question: { id: string | null; slug?: string | null }): string =>
  `/questions/${buildEntitySegment(question)}`;

export const getProfilePath = (profile: { id: string | null; slug?: string | null }): string =>
  `/profile/${buildEntitySegment(profile)}`;

export const getEventEditPath = (event: { id: string | null; slug?: string | null }): string =>
  `${getEventPath(event)}/edit`;

export const getQuestionEditPath = (question: {
  id: string | null;
  slug?: string | null;
}): string => `${getQuestionPath(question)}/edit`;

export const getProfileEditPath = (profile: { id: string | null; slug?: string | null }): string =>
  `${getProfilePath(profile)}/edit`;

export const getEventBigScreenPath = (event: { id: string | null; slug?: string | null }): string =>
  `${getEventPath(event)}/big-screen`;

export const getCommunityPath = (community: { id: string | null; slug?: string | null }): string =>
  `/communities/${buildEntitySegment(community)}`;

export const getChatPath = (user: { id: string | null; slug?: string | null }): string =>
  `/chat/${buildEntitySegment(user)}`;

/**
 * A chat with the author of someone else's post, addressed by the post's id: the server resolves
 * the post's owner, so the path never names the author (TECH-693). The id, not the slug: it is
 * what the chat sends as `question_id`, and a thread goes on even once its post can no longer be
 * resolved by slug.
 */
export const getPostChatPath = (questionId: string): string =>
  `/chat/post/${encodeSegment(questionId)}`;

/**
 * Space slug for "Suggest a Feature" functionality.
 * Navigates to the Meetball Community Space, where users can share feature requests/bug reports.
 */
export const FEATURE_SUGGESTION_COMMUNITY_SLUG = 'meetball-community';

// Legacy helper names retained for compatibility
export const buildEventDetailPath = getEventPath;
export const buildQuestionDetailPath = getQuestionPath;
export const buildProfilePath = getProfilePath;
